// Client-side error reporting infrastructure.
// Catches JS errors (global handlers + React/Preact error boundary),
// queues them in localStorage (survives reloads / offline), and
// fire-and-forget flushes to POST /api/client-log.
//
// Pattern: hackathon-tooling/patterns/testing/client-side-error-reporting.md
// Reference impl: nodns-registrar/src/components/error-boundary.tsx

/** Payload for a single error report, matching the server endpoint schema. */
export interface ErrorReport {
  type: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  timestamp: number;
  userAgent?: string;
  correlationId?: string;
}

const ERROR_QUEUE_KEY = 'nomail_error_queue';
const CORRELATION_ID_KEY = 'nomail_correlation_id';
const MAX_QUEUE_SIZE = 20;
const FLUSH_TIMEOUT_MS = 10_000;

/**
 * Returns a stable per-browser correlation ID so that server logs can
 * group requests belonging to the same session.  Generated lazily on
 * first access and persisted in localStorage.
 */
export function getCorrelationId(): string {
  try {
    let id = localStorage.getItem(CORRELATION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CORRELATION_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode / quota) — fall back to a
    // per-call random ID so the header is always present.
    return crypto.randomUUID();
  }
}

/**
 * Log the error to the console, queue it in localStorage (capped at
 * MAX_QUEUE_SIZE), then fire-and-forget a flush attempt.  Never throws.
 */
export function reportError(report: ErrorReport): void {
  console.error(`[${report.type}]`, report.message, report.stack ?? '');

  try {
    const queue = JSON.parse(
      localStorage.getItem(ERROR_QUEUE_KEY) ?? '[]',
    ) as ErrorReport[];
    queue.push({ ...report, userAgent: navigator.userAgent });
    while (queue.length > MAX_QUEUE_SIZE) queue.shift();
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage full or unavailable — the console.error above still fired.
  }

  void flushErrors();
}

/**
 * Read the queued errors from localStorage and POST them to
 * /api/client-log.  On a 2xx response the queue is cleared.
 * Errors are swallowed silently so callers can fire-and-forget.
 */
export async function flushErrors(): Promise<void> {
  let queue: ErrorReport[];
  try {
    queue = JSON.parse(
      localStorage.getItem(ERROR_QUEUE_KEY) ?? '[]',
    ) as ErrorReport[];
    if (queue.length === 0) return;
  } catch {
    return;
  }

  try {
    const res = await fetch('/api/client-log', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ errors: queue }),
      signal: AbortSignal.timeout(FLUSH_TIMEOUT_MS),
    });
    if (res.ok) {
      localStorage.removeItem(ERROR_QUEUE_KEY);
    }
  } catch {
    // Network failure, timeout, or server error — the queue stays in
    // localStorage and will be retried on the next error or page load.
  }
}

/**
 * Install global listeners for uncaught errors and unhandled promise
 * rejections, and retry any errors queued during a previous session.
 * Safe to call once at app startup (idempotent listeners are fine —
 * the browser dedupes identical listeners on the same target).
 */
export function initGlobalErrorHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportError({
      type: 'window_error',
      message: event.message,
      stack: event.error?.stack ?? '',
      url: event.filename
        ? `${window.location.href} (${event.filename}:${event.lineno}:${event.colno})`
        : window.location.href,
      timestamp: Date.now(),
      correlationId: getCorrelationId(),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    reportError({
      type: 'unhandled_promise_rejection',
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : JSON.stringify(reason),
      stack: reason instanceof Error ? reason.stack ?? '' : '',
      url: window.location.href,
      timestamp: Date.now(),
      correlationId: getCorrelationId(),
    });
  });

  // Retry queued errors from a previous session (offline / network failure).
  void flushErrors();
}
